import Environment from './Environment';
import Features from './Features';

// Kill switch, checked before the experiment so turning it off leaves no
// half-filled experiment behind.
const HOMEPAGE_V2_GATE = '2026-enable_homepage_v2';
// New-vs-old test
const HOMEPAGE_V2_EXPERIMENT = '2026_consumer_homepage_redesign';

/* Which homepage a visitor gets, and with it the navbar treatment that ships
   alongside it. Shared so the two can't disagree — the navbar renders on every
   route, and a second copy of these conditions would drift.

   logExposure MUST stay false everywhere except the homepage itself. The navbar
   is sitewide, so logging there would count PDP-only visitors who never saw
   either homepage and dilute the result toward null.

   Only call once the Statsig client is ready; before that every read returns its
   fallback. */
const showsHomepageV2 = ({ logExposure = false } = {}) => {
  if (Environment.store !== 'bump') {
    return false;
  }

  if (!Features.checkGate(HOMEPAGE_V2_GATE)) {
    return false;
  }

  // Always show new version in dev/QA
  if (Environment.getStatsigEnvironment !== 'production') {
    return true;
  }

  return (
    Features.getExperiment(HOMEPAGE_V2_EXPERIMENT, {
      disableExposureLog: !logExposure,
    }).get('showHomepageV2', false) === true
  );
};

/* Statsig loading statuses that mean "no real values yet". The client emits
   values_updated on every transition, including 'Loading' on the way in, so an
   event arriving is not the same as an answer arriving. */
const STATSIG_PENDING = ['Uninitialized', 'Loading'];

/* Whether showsHomepageV2 can be trusted yet. Callers with different UI per arm
   should hold rather than render a default, or they paint one arm and swap. Any
   status outside the pending list counts — 'Error' included, so a failed init
   reveals the control experience instead of holding forever. Stores with only one
   arm never wait.

   Deliberately stateless: updateUserAsync drops back to 'Loading' on a user
   switch, so callers latch their own copy (`prev.resolved || resolved`) rather
   than re-hiding UI they already committed to. Keeping the latch out of here also
   keeps this module free of state that would leak between tests. */
const variantIsResolved = () =>
  Environment.store !== 'bump' ||
  !STATSIG_PENDING.includes(Features.getClient().loadingStatus);

/* Calls back with the arm and whether it's trustworthy — once immediately, then
   on every Statsig transition. Returns an unsubscribe. Nothing else re-renders on
   Statsig settling, so consumers have to be told.

   `resolved` only ever goes false → true, so consumers don't need to latch it. */
const subscribeToVariant = (fn) => {
  // Per subscription rather than per module: every mount subscribes afresh, and
  // module state here would leak between tests.
  let answered = false;

  const emit = () => {
    const resolved = variantIsResolved();
    /* Re-initializing resets the store and reports 'Loading' before refetching,
       so reads in that window return fallbacks. The homepage does exactly that on
       mount, and passing one of those on would swap the UI to the control arm and
       straight back. Once there's a real answer, stay with it. */
    if (answered && !resolved) return;
    answered = resolved;
    fn({ showsHomepageV2: showsHomepageV2(), resolved });
  };

  Features.getClient().on('values_updated', emit);
  emit();
  return () => Features.getClient().off('values_updated', emit);
};

export default {
  showsHomepageV2,
  variantIsResolved,
  subscribeToVariant,
  HOMEPAGE_V2_GATE,
  HOMEPAGE_V2_EXPERIMENT,
};
